import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';

const BBBaselineChart = lazy(() => import('./components/BBBaselineChart'));
const BettingForm = lazy(() => import('./components/BettingForm'));
const DynamicPanel = lazy(() => import('./components/DynamicPanel'));
const HistoricalTradesLeaderBoardPanel = lazy(() =>
  import('./components/HistoricalTradesLeaderBoardPanel')
);
const GameTimer = lazy(() => import('./components/GameTimer'));
import Loader from './assets/images/loaders/loader.gif';
import AuthError from './assets/images/errors/auth-error.jpg';
import HLBackendV1Api from './utils/http/api';
import { useStore } from './states/store';
import OptionsChart from './components/OptionsChart';
import HLBackendV1 from '../src/utils/socket/HL_backend_v1';
import CountUp from 'react-countup';
import ResultPanel from './components/ResultPanel';
import DrawHistory from './components/DrawHistory';
import UserBetsDetails from './components/UserBetsDetails';
import MobileBettingForm from './components/MobileBettingForm';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import OptionsEChart from './components/OptionsEChart';
import OptionsD3Chart from './components/OptionsD3Chart';
import OptionsHighChart from './components/OptionsHighChart';
import OptionsChartJs from './components/OptionsChartJs';
import { soundLibrary } from './utils/sfx/sound';
import DailyDrawHistory from './components/DailyDrawHistory';
import SettingsEtc from './components/SettingsEtc';
import KBCLogo from './assets/images/company/kbc_logo.png';
import Header from './assets/images/overlays/header.webp';
import { set } from 'date-fns';
import { timer } from 'd3';
import AsymmetricBetCrypto from './crypto/asymmetric-bet-crypto';
import AuthErrorGif from './assets/images/errors/auth-err.gif';

import TradingViewWidget from './components/TradingViewWidget';

const CoinGeckoMarquee = lazy(() => import('./components/CoinGeckoMarquee'));

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { 
    timerStatus,
    authUser, 
    setAuthUser, 
    setCurrentUpBets, 
    setCurrentDownBets, 
    setUpTotalBets, 
    setDownTotalBets, 
    setCurrentUpWager, 
    setCurrentDownWager, 
    isSoundOn, 
    initializeSound, 
    toggleMute,
    setUse,
    setDrawHistoryData,
    setUserBetsDetails,
    betSuccess,
    setBetSuccess,
    setUserPendingBets,
    currentRound,
    setUserBettingResult,
    setUserBettingHistory
  } = useStore();
  const [isAuthUser, setIsAuthUser] = useState(false);
  const [upWager, setUpWager] = useState("100");
  const [downWager, setDownWager] = useState("100");
  const [betType, setBetType] = useState(null);

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [isOptChartVisible, setIsOptChartVisible] = useState(true);
  const [isOptEChartVisible, setIsOptEChartVisible] = useState(true);

  const [uidx, setUidx] = useState(null);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: '#1e293b',
    boxShadow: 24,
    p: 4,
    borderRadius: '16px',
    border: '1px solid rgba(255,255,255,0.1)'
  };

  const getAuth = async() => {
    await HLBackendV1Api.get('/betting-management/checkAuthorization').then((response) => {
      setIsAuthUser(true);
      setAuthUser(response.data)
      setUidx(response.data.uidx);
    }).catch((error) => {
      console.log(error.status);
    }).finally(() => {
      setIsLoading(false);
    });
  }

  useEffect(() => {
    getAuth();
  }, []);

  useEffect(() => {
    if(timerStatus === "draw") {
      setCurrentUpWager(upWager);
      setCurrentDownWager(downWager);
    }
    
    if(timerStatus === "payout") {
      setUpWager("100");
      setDownWager("100");
      setUpTotalBets([]);
      setDownTotalBets([]);
    }

    if(timerStatus !== 'betting_open') {
      handleClose();
    }
  }, [timerStatus]);

  useEffect(() => {

    AsymmetricBetCrypto.initialize().catch(error => {
            console.error('Crypto initialization failed:', error);
        });
    const socket = HLBackendV1.getInstance('/hl_wager').getSocket();

    socket.on('connect', () => {
      socket.on('dynamic_rate', onFetchData);
    });

    return () => {
      socket.off('connect');
      socket.off('dynamic_rate');
    }
  }, []);

  useEffect(() => {
    const userId = uidx || 'anonymous';

    if (userId === 'anonymous') return;

    const appHelperSocket = HLBackendV1.getInstance('/updown_helper', 'null', { userId: userId }).getSocket();

    if(timerStatus === 'betting_open') {
      // Emitters
      appHelperSocket.emit('get_user_pending_bets', { gameId: currentRound?.id });

      // Listeners
      appHelperSocket.on('user_current_pending_bets', ({ results }) => {
        setUserPendingBets(results);
        setBetSuccess(false); // Reset bet success state after fetching pending bets
      });

      setUserBettingResult(null);
    }
    
    if(timerStatus === 'payout') {
      // Emitters
      appHelperSocket.emit('get_daily_users_bets_stats');
      appHelperSocket.emit('get_draw_history');
      appHelperSocket.emit('get_user_betting_results', { gameId: currentRound?.id });

      // Listeners
      appHelperSocket.on('user_betting_results', ({results}) => {
        setUserBettingResult(...results);
      });
      appHelperSocket.on('daily_users_bets_stats', (data) => setUserBetsDetails(data));
      appHelperSocket.on('draw_history', ({data}) => setDrawHistoryData(data));
    }

    appHelperSocket.emit('get_user_betting_history', {});
    appHelperSocket.on('user_betting_history', (data) => {
      setUserBettingHistory(data)
    });
    
    appHelperSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });

     return () => {
      appHelperSocket.off('connect');
      appHelperSocket.off('get_daily_users_bets_stats');
      appHelperSocket.off('get_draw_history');
      appHelperSocket.off('draw_history');
      appHelperSocket.off('daily_users_bets_stats');
      appHelperSocket.off('testu');
      appHelperSocket.off('user_current_pending_bets');
      appHelperSocket.off('get_user_betting_results');
      appHelperSocket.off('user_betting_results');
      appHelperSocket.off('get_user_betting_history');
    }

  }, [uidx, timerStatus, betSuccess]);

  const onFetchData = (data) => {
    setUpWager(data.up);
    setDownWager(data.down);
    setCurrentUpBets(data.upBetLists);
    setCurrentDownBets(data.downBetLists);
    setUpTotalBets(data.totalUpBets);
    setDownTotalBets(data.totalDownBets);
  };

  const closeModal = () => {
    handleClose();
  };

  const toggle = () => {
    setIsOptChartVisible(!isOptChartVisible)
  }

  const toggle1 = () => {
    setIsOptEChartVisible(!isOptChartVisible)
  }

  useEffect(() => {
    initializeSound();
  }, [initializeSound]);

    // Scroll to top function
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Scroll to chart function
    const scrollToChart = () => {
      const chartElement = document.querySelector('.bg-\\[\\#1e293b\\]');
      if (chartElement) {
        chartElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    // Scroll to leaderboard function
    const scrollToLeaderboard = () => {
      const leaderboardElement = document.getElementById('leaderboard');
      if (leaderboardElement) {
        leaderboardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    const toggleChart = () => {
    setIsOptChartVisible(!isOptChartVisible);
  };

  const toggleOdds = () => {
    setIsOptEChartVisible(!isOptEChartVisible);
  };

  return (
    <div className="min-h-screen w-full bg-[#0f172a] text-slate-200 font-sans antialiased selection:bg-cyan-500/30 overflow-x-hidden">
      <DailyDrawHistory />
      
      {isLoading ? (
        <div className="flex flex-col justify-center items-center h-screen">
          <img className="h-40 w-96 object-contain" src={Loader} alt="Loading..." />
          <div className='font-bold text-2xl animate-bounce tracking-widest mt-4'>
            <span className='text-emerald-400'>UP</span>
            <span className='text-slate-500 mx-2'>|</span>
            <span className='text-pink-400'>DOWN</span>
          </div>
        </div>
      ) : (
        <Suspense fallback={
          <div className="flex flex-col justify-center items-center h-screen">
            <img className="h-40 w-96 object-contain" src={Loader} alt="Loading..." />
          </div>
        }>
          {isAuthUser ? (
            <div className="max-w-[1800px] mx-auto p-2 lg:p-4 flex flex-col gap-4">
              
              {/* Header: Marquee */}
              <header className="rounded-2xl bg-slate-800/40 border border-white/5 backdrop-blur-md overflow-hidden shadow-lg">
                <div className="tradingview-widget-container text-red-300">
                  <CoinGeckoMarquee coinIds="bitcoin,ethereum" />
                </div>
              </header>

              {/* ========== STICKY RESPONSIVE DIV ========== */}
              <div className="sticky top-0 z-50 w-full rounded-xl backdrop-blur-md bg-slate-900/95 border border-white/10 shadow-2xl transition-all duration-300">
                <div className="px-3 sm:px-4 py-2 flex items-center justify-between gap-2">

                  {/* Left: Round + Phase Badge */}
                  <div className="flex items-center gap-2 min-w-0">
                    {currentRound && (
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-bold leading-none shrink-0">
                          <span className="text-emerald-400">Round</span>
                          <span className="text-white ml-1">#{currentRound.id}</span>
                        </div>
                        <div className={`hidden sm:flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none ${
                          timerStatus === 'betting_open' ? 'bg-emerald-500/15 text-emerald-400' :
                          timerStatus === 'draw' ? 'bg-pink-500/15 text-pink-400' :
                          'bg-amber-500/15 text-amber-400'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            timerStatus === 'betting_open' ? 'bg-emerald-400 animate-pulse' :
                            timerStatus === 'draw' ? 'bg-pink-400 animate-pulse' :
                            'bg-amber-400'
                          }`} />
                          {timerStatus === 'betting_open' ? 'BETTING' : timerStatus === 'draw' ? 'DRAW' : 'PAYOUT'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Center: Navigation Buttons */}
                  <div className="flex items-center gap-1.5">
                    <button
                      data-tip={isOptChartVisible ? 'Switch to TradingView' : 'Switch to Game Chart'}
                      onClick={toggleChart}
                      className="group px-2.5 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs font-bold transition-all duration-200 flex items-center gap-1.5 hover:scale-105 active:scale-95 hover:shadow-[0_0_12px_rgba(6,182,212,0.15)]"
                    >
                      {isOptChartVisible ? (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                        </svg>
                      )}
                      <span className="hidden sm:inline">{isOptChartVisible ? 'Pro' : 'Game'}</span>
                    </button>

                    <button
                      data-tip="Settings & Tutorial"
                      onClick={() => document.getElementById('my_modal_2').showModal()}
                      className="group px-2.5 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 text-xs font-bold transition-all duration-200 flex items-center gap-1.5 hover:scale-105 active:scale-95 hover:shadow-[0_0_12px_rgba(168,85,247,0.15)]"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 group-hover:rotate-90 transition-transform duration-500">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="hidden sm:inline">Settings</span>
                    </button>
                  </div>

                  {/* Right: User Info */}
                  <div className="flex items-center gap-2 min-w-0">
                    {authUser && (
                      <div className="flex items-center gap-1.5 bg-white/[0.03] rounded-lg px-2 py-1">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-[10px] font-bold shadow-lg shrink-0">
                          {authUser.nick ? authUser.nick.charAt(0).toUpperCase() : 'U'}
                        </div>
                        {authUser.balance !== undefined && (
                          <div className="text-xs font-bold text-emerald-400 tabular-nums leading-none truncate max-w-[100px]">
                            ₿ {authUser.balance.toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Phase-colored Pulse Bar */}
                <div className={`h-0.5 rounded-b-xl relative overflow-hidden ${
                  timerStatus === 'betting_open' ? 'bg-emerald-500/20' :
                  timerStatus === 'draw' ? 'bg-pink-500/20' :
                  'bg-amber-500/20'
                }`}>
                  <div className={`absolute inset-0 ${
                    timerStatus === 'betting_open' ? 'bg-gradient-to-r from-transparent via-emerald-400/80 to-transparent' :
                    timerStatus === 'draw' ? 'bg-gradient-to-r from-transparent via-pink-400/80 to-transparent' :
                    'bg-gradient-to-r from-transparent via-amber-400/80 to-transparent'
                  } animate-shimmer`} />
                </div>
              </div>
              {/* ========== END STICKY RESPONSIVE DIV ========== */}

              <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                
                {/* Left Column (Chart, Stats, Betting) */}
                <section className="xl:col-span-8 flex flex-col gap-4">
                  
                  {/* Stats & Timer Glass Card */}
                  <div className={`bg-slate-800/40 border border-white/10 rounded-xl p-4 sm:p-6 relative overflow-hidden backdrop-blur-md shadow-2xl transition-all duration-500 ${timerStatus === "payout" && "hidden"}`}>
                    
                    {/* Settings/Tutorial Trigger */}
                    <div className="hidden absolute top-4 right-4 z-20 cursor-pointer text-slate-200 hover:text-white transition-colors bg-violet-700/90 rounded-full drop-shadow-xl/50 hover:bg-indigo-700 tooltip tooltip-left" data-tip="Settings and Tutorial" onClick={() => document.getElementById('my_modal_2').showModal()}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8">
                        <path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm6 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" clipRule="evenodd" />
                      </svg>
                    </div>

                    <dialog id="my_modal_2" className="modal">
                      <SettingsEtc />
                      <form method="dialog" className="modal-backdrop">
                       <button>close</button>
                      </form>
                    </dialog>

                    {/* Ambient Glows */}
                    <div className="absolute top-0 left-0 w-48 h-48 bg-emerald-500/50 blur-[100px] pointer-events-none" />
                    <div className="absolute bottom-0 right-0 w-48 h-48 bg-pink-500/50 blur-[100px] pointer-events-none" />
                    <img src={Header} alt="Winner" className={"absolute top-0 left-0 w-full object-cover h-full opacity-50 max-sm:opacity-40"} />
                    <div className="flex flex-row items-center justify-between relative z-10 w-full gap-2">               
                      {/* Up Odds */}
                      <div className="flex-1 flex flex-col items-center sm:items-end justify-center drop-shadow-xl/100">
                        <span className='text-emerald-400/100 text-md sm:text-sm font-extrabold  uppercase cursor-pointer mb-1 drop-shadow-lg/100' onClick={toggle1}>
                          업 당첨
                        </span>
                        <span className="text-6xl max-sm:text-xl font-black bg-gradient-to-b from-emerald-400 to-green-500 bg-clip-text text-transparent tabular-nums">
                          {upWager !== 0 ? upWager : 100}%
                        </span>
                      </div>

                      {/* Timer */}
                      <div className="flex-1 flex justify-center relative scale-90 sm:scale-100">
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-20 h-20 rounded-full border border-emerald-500/20 animate-ping" style={{ animationDuration: '3s' }} />
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <div className="w-16 h-16 rounded-full border border-pink-500/20 animate-ping" style={{ animationDuration: '4s', animationDelay: '1s' }} />
                        </div>
                        <div className="relative z-10 transform hover:scale-105 transition-all duration-300">
                          <GameTimer />
                        </div>
                      </div>
                      {/* Down Odds */}
                      <div className="flex-1 flex flex-col items-center sm:items-start justify-center drop-shadow-xl/100">
                        <span className='text-pink-400/100 text-md sm:text-sm font-extrabold uppercase cursor-pointer mb-1 drop-shadow-lg/100' onClick={toggle}>
                          다운 당첨
                        </span>
                        <span className="text-6xl max-sm:text-xl font-black bg-gradient-to-b from-pink-400 to-rose-500 bg-clip-text text-transparent tabular-nums">
                          {downWager !== 0 ? downWager : 100}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Chart Container */}
                  <div className="bg-[#1e293b] rounded-xl border border-white/5 shadow-[inset_0_2px_20px_rgba(0,0,0,0.5)] overflow-hidden relative">
                  <div className={`w-full h-[30rem] ${isOptChartVisible && 'hidden'}`}>
                      {/* {isOptChartVisible ? <OptionsHighChart /> : <OptionsChart />} */}
                      <TradingViewWidget />
                    </div>
                    <div className={`w-full ${!isOptChartVisible && 'hidden'}`}>
                      <OptionsHighChart />
                    </div>
                  </div>

                  {/* Desktop Betting Buttons */}
                  <AnimatePresence>
                    {timerStatus === "betting_open" && (
                      <motion.div 
                        initial={{ y: 20, opacity: 0, height: 0 }}
                        animate={{ y: 0, opacity: 1, height: 'auto' }}
                        exit={{ y: 20, opacity: 0, height: 0 }}
                        className="hidden sm:flex flex-row gap-2 w-full"
                      >
                        <button 
                            className="flex-1 group relative overflow-hidden rounded-l-xl h-16 border border-white/10 font-black text-xl text-white transition-all duration-75 active:scale-95 shadow-xl cursor-pointer [clip-path:polygon(0%_0%,100%_0%,90%_100%,0%_100%)]"
                            onClick={() => {
                                handleOpen();
                                setBetType("up");
                                soundLibrary.newBets.play();
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 to-teal-700" />
                            <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-teal-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl" />
                            <div className="relative flex items-center justify-center gap-3 z-10">
                                <div className="p-2 rounded-xl bg-white/10 group-hover:bg-white/20 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 drop-shadow-md">
                                        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm.53 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v5.69a.75.75 0 0 0 1.5 0v-5.69l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="uppercase tracking-widest drop-shadow-md">업</span>
                            </div>
                        </button>

                        <button 
                            className="flex-1 group relative overflow-hidden rounded-r-xl h-16 border border-white/10 font-black text-xl text-white transition-all duration-75 active:scale-95 shadow-xl cursor-pointer -ml-[5%] [clip-path:polygon(10%_0,100%_0,100%_100%,0_100%)]"
                            onClick={() => {
                                handleOpen();
                                setBetType("down");
                                soundLibrary.newBets.play();
                            }}
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-pink-600 to-rose-700" />
                            <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-rose-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-2xl" />
                            <div className="relative flex items-center justify-center gap-3 z-10">
                                <div className="p-2 rounded-xl bg-white/10 group-hover:bg-white/20 transition-all duration-300 group-hover:-rotate-12 group-hover:scale-110">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 drop-shadow-md">
                                        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-.53 14.03a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V8.25a.75.75 0 0 0-1.5 0v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3Z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <span className="uppercase tracking-widest drop-shadow-md">다운</span>
                            </div>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Dynamic panel for small screens (inserted below charts on mobile) */}
                  <div className="w-full bg-slate-800/40 border border-white/10 rounded-xl backdrop-blur-md overflow-hidden xl:hidden">
                    <DynamicPanel />
                  </div>
                </section>

                {/* Right Column: Dynamic Panel (Desktop) */}
                <aside className="xl:col-span-4 hidden xl:block">
                <div className="h-full w-full bg-slate-800/40 border border-white/10 rounded-xl backdrop-blur-md overflow-hidden shadow-2xl flex flex-col">
                  <DynamicPanel />
                </div>
              </aside>

              </div>

              {/* Bottom Footer: Leaderboard */}
              <footer id="leaderboard" className="bg-slate-800/40 mb-10 border border-white/10 rounded-xl p-4 sm:p-6 backdrop-blur-md overflow-auto min-h-[400px] shadow-2xl transition-all duration-500 ease-in-out">
                <HistoricalTradesLeaderBoardPanel />
              </footer>

            </div>
          ) : (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f172a] p-4">
                <div className="relative p-8 rounded-3xl bg-slate-800/50 backdrop-blur-xl border border-white/10 shadow-2xl w-full max-w-md">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-rose-500 to-pink-500 rounded-3xl opacity-20 blur-xl animate-pulse" />
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl opacity-10 blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
                    <div className="relative">
                        <img src={AuthErrorGif} className='h-30 w-30 mx-auto drop-shadow-2xl' />
                        {/* <img className="w-full h-auto object-contain mx-auto drop-shadow-2xl" src={AuthError} alt="Authentication Error" /> */}
                        <p className='text-center drop-shadow-2xl text-lg font-semibold'>로그인 정보를 찾을수가 없습니다.</p><br/>
                        <p className='text-center drop-shadow-2xl text-md font-semibold -pt-2'>다시 로그인해 주세요.</p>
                        <p className="text-center text-white/70 mt-6 text-sm font-medium">Please sign in to continue</p>
                        <div className="flex justify-center mt-4">
                            <div className="w-2 h-2 bg-rose-500 rounded-full animate-pulse mx-1" />
                            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse mx-1" style={{ animationDelay: '0.2s' }} />
                            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse mx-1" style={{ animationDelay: '0.4s' }} />
                        </div>
                    </div>
                </div>
            </div>
          )}

          {/* Mobile Betting Modal */}
          <Modal open={open} onClose={handleClose}>
              <Box sx={style}>
                  <button onClick={handleClose} className="absolute top-4 right-4 z-10 text-white/50 hover:text-white transition-colors bg-white/5 rounded-full p-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                  </button>
                  <MobileBettingForm type={betType} onClose={closeModal}/>
                  {/* <img src={KBCLogo} alt="Winner" className={"block mx-auto h-fit opacity-90 object-contain size-24 mt-2"}/> */}
              </Box>
          </Modal>

          {/* Mobile Floating Dock */}
          {isAuthUser && (
              <div className={`fixed bottom-0 z-40 w-full flex justify-center px-2 sm:hidden transition-all duration-500 ease-in-out transform ${
                  timerStatus !== "betting_open" ? 'opacity-0 translate-y-full pointer-events-none' : 'opacity-100 translate-y-0'
              }`}>
                  <div className="flex w-full max-w-md rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/10 backdrop-blur-xl bg-slate-900/80">
                      <button
                          className="flex-1 group relative flex flex-row items-center justify-center py-3 text-white transition-all duration-75 active:scale-95 [clip-path:polygon(0%_0%,100%_0%,80%_100%,0%_100%)]"
                          onClick={() => {
                              handleOpen();
                              setBetType("up");
                              soundLibrary.newBets.play();
                          }}
                      >
                          <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/80 to-teal-700/80" />
                          <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent opacity-0 group-active:opacity-100 transition-opacity duration-300" />
                          <div className="p-2 rounded-xl bg-white/10 group-hover:bg-white/20 transition-all duration-300 group-hover:rotate-12 group-hover:scale-110">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 drop-shadow-md">
                                  <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm.53 5.47a.75.75 0 0 0-1.06 0l-3 3a.75.75 0 1 0 1.06 1.06l1.72-1.72v5.69a.75.75 0 0 0 1.5 0v-5.69l1.72 1.72a.75.75 0 1 0 1.06-1.06l-3-3Z" clipRule="evenodd" />
                              </svg>
                          </div>
                          <span className="relative z-10 uppercase font-black text-xl tracking-wider drop-shadow-md">업</span>
                      </button>

                      {/* <div className="w-px bg-white/20 relative z-20" /> */}

                      <button
                          className="flex-1 group relative flex flex-row items-center justify-center py-3 text-white transition-all duration-75 active:scale-95 -ml-[9%] [clip-path:polygon(20%_0,100%_0,100%_100%,0_100%)]"
                          onClick={() => {
                              handleOpen();
                              setBetType("down");
                              soundLibrary.newBets.play();
                          }}
                      >
                          <div className="absolute inset-0 bg-gradient-to-br from-pink-600/80 to-rose-700/80" />
                          <div className="absolute inset-0 bg-gradient-to-t from-white/20 to-transparent opacity-0 group-active:opacity-100 transition-opacity duration-300" />
                          <div className="p-2 rounded-xl bg-white/10 group-hover:bg-white/20 transition-all duration-300 group-hover:-rotate-12 group-hover:scale-110">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 drop-shadow-md">
                                  <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-.53 14.03a.75.75 0 0 0 1.06 0l3-3a.75.75 0 1 0-1.06-1.06l-1.72 1.72V8.25a.75.75 0 0 0-1.5 0v5.69l-1.72-1.72a.75.75 0 0 0-1.06 1.06l3 3Z" clipRule="evenodd" />
                              </svg>
                          </div>
                          <span className="relative z-10 uppercase font-black text-xl tracking-wider drop-shadow-md">다운</span>
                      </button>
                  </div>
              </div>
          )}

        </Suspense>
      )}
    </div>
  );
};

export default App;